import {
  getDocs,
  doc,
  getDoc,
  runTransaction,
  setDoc,
  updateDoc,
  onSnapshot,
  arrayUnion,
  serverTimestamp,
  collection,
  query,
  where,
} from 'firebase/firestore';
import { db } from './config';
import { callLookupCompanyOfUser } from './functions';

// A "company" is a shared PROVIDER identity: the name/logo/contact/terms that go
// on invoices, plus a roster of members. Colleagues at the same barn/business
// link into one company so their charges & notes group together and bill under
// one name — for ALL current and future horses, not horse-by-horse.
//
// A user founds their own company the first time they save Business Info. They
// can also be linked into a colleague's company. One identity per person: if a
// user somehow belongs to more than one, a company they JOINED wins over the one
// they founded.

const SHARED_FIELDS = ['name', 'email', 'phone', 'address', 'terms', 'logoUrl', 'logoPath'];

const pickShared = (fields) => {
  const out = {};
  SHARED_FIELDS.forEach((k) => { if (fields[k] !== undefined) out[k] = fields[k] || ''; });
  return out;
};

// Live company the user belongs to (resolved by membership, so a linked
// colleague adopts the shared identity automatically). null if they have none
// yet. On a transient error we keep the last-known company rather than clearing.
export const subscribeToMyCompany = (uid, cb) => {
  if (!uid) { cb(null); return () => {}; }
  const q = query(collection(db, 'companies'), where('memberUids', 'array-contains', uid));
  return onSnapshot(q, (snap) => {
    if (snap.empty) { cb(null); return; }
    const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    cb(docs.find((c) => c.founderUid !== uid) || docs[0]);
  }, () => { /* keep last-known company on transient error */ });
};

// The company someone ELSE belongs to — used by a horse owner sharing with a
// provider, to offer the rest of that provider's team. Same founder preference
// as subscribeToMyCompany.
export const fetchCompanyOfUser = async (uid) => {
  if (!uid) return null;
  return callLookupCompanyOfUser(uid);
};

export const subscribeToCompanyById = (companyId, cb) => {
  if (!companyId) { cb(null); return () => {}; }
  return onSnapshot(doc(db, 'companies', companyId), (snap) => {
    cb(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  }, () => { /* keep last-known */ });
};

// Create the company (first save) or update its shared profile. Any member may
// edit; the founder is fixed at creation. `member` = { uid, name, email } of the
// caller, recorded on the roster when the company is first created. Returns the
// company id.
export const saveCompanyProfile = async (uid, existingCompany, member, fields) => {
  const shared = pickShared(fields);
  if (existingCompany?.id) {
    await updateDoc(doc(db, 'companies', existingCompany.id), { ...shared, updatedAt: serverTimestamp() });
    return existingCompany.id;
  }
  const companyId = 'co_' + uid;
  // Carry the founder's invoice sequence onto the company, so numbering
  // continues rather than starting again at INV-0001.
  let seedCounter = 0;
  try {
    const mine = await getDoc(doc(db, 'userSettings', uid));
    seedCounter = (mine.exists() && mine.data().invoiceCounter) || 0;
  } catch (e) { /* a fresh sequence is the safe fallback */ }
  await setDoc(doc(db, 'companies', companyId), {
    ...shared,
    founderUid: uid,
    memberUids: [uid],
    members: [member],
    invoiceCounter: seedCounter,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return companyId;
};

// Link a colleague into the company for all current and future work.
export const addCompanyMember = (companyId, member) =>
  updateDoc(doc(db, 'companies', companyId), {
    memberUids: arrayUnion(member.uid),
    members: arrayUnion(member),
    updatedAt: serverTimestamp(),
  });

// Someone joining a company brings their own invoice history with them. If they
// had reached INV-0012 alone and the company is only at INV-0003, the company
// must jump ahead — otherwise it would re-issue numbers this member has already
// given a client, and two horses would carry the same invoice number.
//
// This runs on the JOINING member's device because the security rules let a user
// read only their own settings: nobody else can see their counter. A member may
// update the company, so they raise it themselves. A no-op once they match.
export const reconcileInvoiceCounter = async (uid, companyId) => {
  if (!uid || !companyId) return;
  let mine = 0;
  try {
    const s = await getDoc(doc(db, 'userSettings', uid));
    mine = (s.exists() && s.data().invoiceCounter) || 0;
  } catch (e) { return; }
  if (mine <= 0) return;
  try {
    await runTransaction(db, async (tx) => {
      const ref = doc(db, 'companies', companyId);
      const snap = await tx.get(ref);
      if (!snap.exists()) return;
      const ours = snap.data().invoiceCounter || 0;
      if (ours >= mine) return;
      tx.update(ref, { invoiceCounter: mine, updatedAt: serverTimestamp() });
    });
  } catch (e) { /* a failed catch-up is retried next time the company loads */ }
};

// Remove a member. Member objects need an exact match for arrayRemove, so we
// rewrite both arrays from the known current state instead.
export const removeCompanyMember = (company, uid) =>
  updateDoc(doc(db, 'companies', company.id), {
    memberUids: (company.memberUids || []).filter((u) => u !== uid),
    members: (company.members || []).filter((m) => m.uid !== uid),
    updatedAt: serverTimestamp(),
  });
